// The conversation, in the prototype's look: boss bubbles on the right, employee bubbles on the left,
// tool lines (spinner, then ✓ or ✕) in mono, and centred system notes.
//
// A message is one of:
//   { kind: 'boss', text, viaVoice, attachments: [{ name, size, ext }] }
//   { kind: 'agent', agentId, text }
//   { kind: 'tool', label, detail, state: 'run' | 'ok' | 'fail', result }
//   { kind: 'sys', text, err }
//   { kind: 'typing', agentId }
//   { kind: 'approval', approval }  — an approval card (Approve / Change / Reject)
//   { kind: 'question', agentId, question, options } — the employee asks; options are buttons (prototype .opts)
//   { kind: 'memory', facts, shared } — "SAVED TO MEMORY" card (prototype .memcard)
import { useEffect, useRef } from 'react';
import Avatar from '../Avatar';
import ApprovalCard from './ApprovalCard';

// A reply can hold a simple pipe table ("| Place | Cash in hand |", then "|---|---|", one row per line). It is
// shown as a real table inside the bubble. Until the separator line has arrived, it stays plain text.
const SEP = /^\|?\s*:?-{3,}[\s:|-]*$/;
const NUM = /^[৳$]?\s?[-\d০-৯,.]+%?$|৳/;
const TOTAL = /^(total|মোট)/i;
const cells = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim().replace(/\*\*/g, ''));

function splitBlocks(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let buf = [];
  const flush = () => {
    const t = buf.join('\n').replace(/^\n+|\n+$/g, '');
    if (t) out.push({ text: t });
    buf = [];
  };
  for (let i = 0; i < lines.length;) {
    if (lines[i].trim().startsWith('|') && i + 1 < lines.length && SEP.test(lines[i + 1].trim())) {
      let j = i + 2;
      while (j < lines.length && lines[j].trim().startsWith('|')) j++;
      flush();
      out.push({ table: [lines[i], ...lines.slice(i + 2, j)].map(cells) });
      i = j;
    } else {
      buf.push(lines[i]);
      i++;
    }
  }
  flush();
  return out;
}

function ReplyTable({ rows }) {
  const [head, ...body] = rows;
  const last = body[body.length - 1];
  const total = body.length > 1 && TOTAL.test(last[0] || '') ? last : null;
  const main = total ? body.slice(0, -1) : body;
  const fit = (r) => head.map((_, i) => r[i] ?? '');
  const numCol = head.map((_, i) => i > 0 && body.some((r) => r[i]) && body.every((r) => !r[i] || NUM.test(r[i])));
  const cls = (i) => (numCol[i] ? 'num' : '');
  return (
    <div className="cardwrap"><div className="tblw"><table>
      <thead><tr>{head.map((h, i) => <th key={i} className={cls(i)}>{h}</th>)}</tr></thead>
      <tbody>{main.map((r, j) => <tr key={j}>{fit(r).map((c, i) => <td key={i} className={cls(i)}>{c}</td>)}</tr>)}</tbody>
      {total && <tfoot><tr>{fit(total).map((c, i) => <td key={i} className={cls(i)}>{c}</td>)}</tr></tfoot>}
    </table></div></div>
  );
}

// An employee's words: plain text, with any pipe tables drawn as tables.
export function ReplyText({ text }) {
  const blocks = splitBlocks(text);
  if (!blocks.some((b) => b.table)) return text || null;
  return blocks.map((b, k) => (b.table ? <ReplyTable key={k} rows={b.table} /> : <span key={k} style={{ display: 'block' }}>{b.text}</span>));
}

const MemCard = ({ facts, shared }) => (
  <div className="memcard"><b>SAVED TO MEMORY{shared ? ' · WHOLE OFFICE' : ''}</b>{facts.map((f, j) => <span key={j}>{f}</span>)}</div>
);

export default function MessageList({ messages, agentsById, chatAgentId, onDecide, onAnswer, onRetry, speakingId }) {
  // The reply being read aloud gets the little sound-wave mark (prototype .speaking).
  const speakingIndex = speakingId ? messages.map((m) => m.kind === 'agent' && m.agentId === speakingId).lastIndexOf(true) : -1;
  // A question can be answered while nothing the boss said came after it; the chosen option stays highlighted.
  const lastBossAfter = (i) => messages.slice(i + 1).find((m) => m.kind === 'boss');
  const lastRetry = messages.map((m) => !!(m.kind === 'sys' && m.retry)).lastIndexOf(true);
  // Picking an option is sent as a normal message, but the chosen button already shows it: no extra bubble.
  const hiddenAnswers = new Set();
  messages.forEach((m, i) => {
    if (m.kind !== 'question') return;
    const j = messages.findIndex((x, k) => k > i && x.kind === 'boss');
    const b = messages[j];
    if (b && m.options.includes(b.text) && !(b.attachments || []).length && !b.viaVoice) hiddenAnswers.add(j);
  });
  // A "saved to memory" card goes inside the reply that follows it, as in the design. Until that reply comes
  // (or if it never does), the card shows on its own.
  const memsFor = {};
  const attached = new Set();
  messages.forEach((m, i) => {
    if (m.kind !== 'memory') return;
    for (let k = i + 1; k < messages.length; k++) {
      const x = messages[k];
      if (x.kind === 'typing' || x.kind === 'tool' || x.kind === 'memory') continue;
      if (x.kind === 'agent' && (!m.agentId || x.agentId === m.agentId)) {
        const cards = memsFor[k] || (memsFor[k] = []);
        const prev = cards[cards.length - 1];
        if (prev && prev.shared === !!m.shared) prev.facts = [...prev.facts, ...m.facts];
        else cards.push({ facts: m.facts, shared: !!m.shared });
        attached.add(i);
      }
      break;
    }
  });
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [messages]);

  return (
    <div className="msgs" ref={ref} aria-live="polite">
      {messages.map((m, i) => {
        if (m.kind === 'boss') {
          if (hiddenAnswers.has(i)) return null;
          return (
            <div className="aio-row me" key={i}>
              <div className="m">{m.viaVoice && <span className="via">Said by voice</span>}{m.text}
                {(m.attachments || []).map((f, j) => <div className="file" key={j}><i>{f.ext}</i><span>{f.name} · {f.size}</span></div>)}
              </div>
            </div>
          );
        }
        if (m.kind === 'agent' || m.kind === 'typing') {
          const a = agentsById[m.agentId];
          return (
            <div className="aio-row ag" key={i}>
              <Avatar agent={a} size="sm" />
              {m.kind === 'typing'
                ? <div className="m typing"><i /><i /><i /></div>
                : (
                  <div className={`m ${i === speakingIndex ? 'speaking' : ''}`}>
                    {m.agentId !== chatAgentId && a && <span className="via">{a.name}</span>}
                    <ReplyText text={m.text} />
                    {memsFor[i] && <div className="cardwrap">{memsFor[i].map((c, j) => <MemCard key={j} {...c} />)}</div>}
                  </div>
                )}
            </div>
          );
        }
        if (m.kind === 'tool') {
          return (
            <div className={`tool ${m.state || 'run'}`} key={i}>
              <span className="ic">{m.state === 'ok' ? '✓' : m.state === 'fail' ? '✕' : ''}</span>
              <span><b>{m.label}</b>{m.detail ? ` · ${m.detail}` : ''}{m.result ? <span className="res"> — {m.result}</span> : null}</span>
            </div>
          );
        }
        if (m.kind === 'question') {
          const a = agentsById[m.agentId];
          const answer = lastBossAfter(i);
          const open = !answer && !!onAnswer;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="aio-row ag"><Avatar agent={a} size="sm" /><div className="m">{m.agentId !== chatAgentId && a && <span className="via">{a.name}</span>}<ReplyText text={m.question} /></div></div>
              {m.options.length > 0 && (
                <div className="opts">
                  {m.options.map((o) => (
                    <button key={o} disabled={!open} className={answer && answer.text === o ? 'chosen' : ''} onClick={() => open && onAnswer(o)}>{o}</button>
                  ))}
                </div>
              )}
            </div>
          );
        }
        if (m.kind === 'memory') {
          if (attached.has(i)) return null;
          return (
            <div className="aio-row ag" key={i}>
              <Avatar agent={agentsById[m.agentId || chatAgentId]} size="sm" />
              <MemCard facts={m.facts} shared={m.shared} />
            </div>
          );
        }
        if (m.kind === 'sys' && m.retry) {
          return (
            <div className="sys err" key={i}>
              {m.text}
              {i === lastRetry && onRetry && <button className="aio-btn" onClick={onRetry}>Try again</button>}
            </div>
          );
        }
        if (m.kind === 'approval') {
          return <ApprovalCard key={`ap${m.approval.id}`} item={m.approval} agent={agentsById[m.approval.agentId]} inChat onDecide={onDecide} />;
        }
        return <div className={`sys ${m.err ? 'err' : ''}`} key={i}>{m.text}</div>;
      })}
    </div>
  );
}
